var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { SignalWatcher, effect } from "@lit-labs/preact-signals";
import { scenario, draft } from "../state";
import { api } from "../api/client";
import "../shell/pas-banner";
import "../shell/chart-navigator";
import "../components/chart-viewer";
import "../modes/classify";
import "../modes/abstract";
import "../modes/audit";
import "../modes/sequence";
import "../modes/query";
import "./own-work-view";
function blankDraft() {
    return { codes: [], episodeMeta: {}, navigateAnswers: {}, classifications: {}, query_text: "", saveStatus: "idle" };
}
let BatchView = class BatchView extends SignalWatcher(LitElement) {
    constructor() {
        super(...arguments);
        this.batchId = "";
        this.batchTitle = "";
        this.episodes = [];
        this.idx = 0;
        this.selectedDocId = null;
        this.saveStatus = "idle";
        this.result = null;
        this.loadError = "";
        this.submitting = false;
        // Distinct from loadError: a failed submit keeps the student on the editing
        // view (their drafts are safe) rather than replacing it with an error screen.
        this.submitError = "";
        this.showSubmitConfirm = false;
        this.submitAnyway = false;
        // Set when the batch is the own-work coding audit: rendered by <own-work-view> instead.
        this.ownWork = null;
        this.drafts = {};
        this._switching = false;
        this._stopEffect = null;
        this._saveTimer = null;
    }
    static { this.styles = css `
    :host, *, *::before, *::after { box-sizing: border-box; }
    :host { display: grid; grid-template-rows: auto auto 1fr auto; height: 100vh;
            container-type: inline-size; container-name: ehr;
            border-radius: 10px; overflow: hidden;
            background: var(--color-slate-50, #f8fafc); }
    .stepper { display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
               padding: 10px 16px; background: var(--color-white); border-bottom: 1px solid var(--color-slate-200); }
    .stepper .title { font-weight: 700; font-size: 14px; color: var(--color-slate-800); }
    .stepper .count { font-size: 12px; color: var(--color-slate-500); }
    .dots { display: flex; gap: 6px; margin-left: auto; flex-wrap: wrap; }
    .dot { width: 26px; height: 26px; border-radius: 50%; border: 1px solid var(--color-slate-300);
           background: var(--color-white); color: var(--color-slate-600); font-size: 12px; cursor: pointer; }
    .dot.current { background: var(--color-blue-600, #2563eb); color: white; border-color: var(--color-blue-600, #2563eb); }
    .dot.done { border-color: var(--color-green-500, #16a34a); color: var(--color-green-600, #15803d); }
    .ep-title { padding: 8px 16px; background: var(--color-white); border-bottom: 1px solid var(--color-slate-200);
                font-size: 13px; color: var(--color-slate-600); }
    .ep-title b { color: var(--color-slate-900); }
    .body { display: grid; grid-template-columns: 184px minmax(0, 1fr) minmax(0, 420px);
            gap: 0; overflow: hidden; min-height: 0; }
    .pane { overflow: auto; min-height: 0; min-width: 0; }
    .pane.nav { background: var(--color-slate-100); border-right: 1px solid var(--color-slate-200); }
    .pane.chart { background: var(--color-white); padding: 18px 20px; }
    .pane.panel { background: var(--color-slate-50); border-left: 1px solid var(--color-slate-200); padding: 16px; }
    /* Stacked: .body (a descendant, display:block) is the scroll container — see
       case-view for the why (container query can't style :host; a fixed-height
       grid compresses rows). Stepper + actions stay pinned. */
    @container ehr (max-width: 759px) {
      .body { display: block; overflow-y: auto; overflow-x: hidden;
              background: var(--color-slate-200); }
      .pane { overflow: visible; border: 0; }
      .pane + .pane { margin-top: 8px; }
    }
    .actions { display: flex; gap: 12px; align-items: center; padding: 12px 16px;
               background: var(--color-white); border-top: 1px solid var(--color-slate-200); }
    .save { font-size: 13px; color: var(--color-slate-500); }
    .submit-error { font-size: 13px; color: var(--color-danger, #dc2626); max-width: 320px; }
    .spacer { flex: 1; }
    button { font-family: inherit; }
    button.nav-btn { padding: 9px 16px; border-radius: var(--radius-md, 6px); background: var(--color-white);
                     color: var(--color-slate-700); border: 1px solid var(--color-slate-300); cursor: pointer; font-weight: 600; }
    button.nav-btn:disabled { opacity: .5; cursor: default; }
    button.submit { padding: 10px 20px; border-radius: var(--radius-md, 6px); background: var(--color-blue-600, #2563eb);
                    color: white; border: 0; cursor: pointer; font-weight: 600; }
    button.submit:disabled { opacity: .6; cursor: default; }
    .empty { color: var(--color-slate-500); font-style: italic; padding: 24px; }
    .done-card { max-width: 640px; margin: 48px auto; background: var(--color-white); border: 1px solid var(--color-slate-200);
                 border-radius: 10px; padding: 28px; text-align: center; }
    .done-card h2 { margin: 0 0 8px; color: var(--color-slate-900); }
    .done-card p { color: var(--color-slate-600); line-height: 1.55; }

    /* submit confirmation dialog */
    .confirm-backdrop { position: fixed; inset: 0; background: rgba(15,23,42,.6);
                         display: grid; place-items: center; z-index: 200; }
    .confirm-modal { background: var(--color-white); padding: 22px; border-radius: 8px;
                      max-width: 420px; width: 90vw; box-shadow: 0 12px 32px rgba(0,0,0,.25); }
    .confirm-modal h2 { margin: 0 0 12px; font-size: 16px; color: var(--color-slate-900); }
    .confirm-list { list-style: none; margin: 0 0 14px; padding: 0; max-height: 260px; overflow-y: auto; }
    .confirm-list li { display: flex; justify-content: space-between; gap: 12px; padding: 7px 0;
                        border-bottom: 1px solid var(--color-slate-100); font-size: 13px;
                        color: var(--color-slate-700); }
    .confirm-status { flex: 0 0 auto; font-weight: 700; font-size: 11px; text-transform: uppercase;
                       letter-spacing: .04em; }
    .confirm-status.filled { color: var(--color-green-600, #15803d); }
    .confirm-status.empty { color: var(--color-amber-600, #d97706); }
    .confirm-warning { font-size: 13px; color: var(--color-slate-600); line-height: 1.5; margin: 0 0 10px; }
    .confirm-anyway { display: flex; align-items: flex-start; gap: 8px; font-size: 13px;
                       color: var(--color-slate-700); margin: 0 0 16px; }
    .confirm-actions { display: flex; justify-content: flex-end; gap: 10px; }
  `; }
    async connectedCallback() {
        super.connectedCallback();
        try {
            const b = await api.getBatch(this.batchId);
            if (b.kind === "own-work-audit") {
                this.ownWork = b;
                return;
            }
            this.batchTitle = b.title;
            this.episodes = b.episodes ?? [];
            // hydrate saved drafts for resume
            let saved = {};
            try {
                const d = await api.loadDraft(this.batchId);
                const dd = d?.draft?.drafts;
                if (dd)
                    saved = dd;
            }
            catch { /* no saved draft */ }
            for (const ep of this.episodes)
                this.drafts[ep.scenario_id] = saved[ep.scenario_id] ?? blankDraft();
            this._activate(0);
            this._stopEffect = effect(() => {
                const d = draft.value;
                if (this._switching || !d)
                    return;
                const sid = this.episodes[this.idx]?.scenario_id;
                if (sid) {
                    this.drafts[sid] = d;
                    this._scheduleSave();
                    // this.drafts is a plain field, not reactive state, and _scheduleSave's
                    // saveStatus assignment is a no-op re-render trigger whenever the status
                    // was already "saving" (e.g. two edits inside the same debounce window) —
                    // request an update explicitly so stepper dot "done" state never lags.
                    this.requestUpdate();
                }
            });
        }
        catch (e) {
            this.loadError = e.message;
        }
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        if (this._stopEffect)
            this._stopEffect();
        if (this._saveTimer)
            clearTimeout(this._saveTimer);
    }
    _activate(i) {
        this._switching = true;
        this.idx = i;
        this.selectedDocId = null;
        const ep = this.episodes[i];
        scenario.value = ep;
        draft.value = this.drafts[ep.scenario_id];
        this._switching = false;
    }
    _scheduleSave() {
        this.saveStatus = "saving";
        if (this._saveTimer)
            clearTimeout(this._saveTimer);
        this._saveTimer = setTimeout(() => void this._save(), 1500);
    }
    async _save() {
        try {
            await api.saveDraft(this.batchId, { drafts: this.drafts });
            this.saveStatus = "saved";
        }
        catch {
            this.saveStatus = "error";
        }
    }
    onDocFocus(e) { this.selectedDocId = e.detail.docId; }
    /** An episode counts as "done" once the student has put something in its
     *  draft. Batches are currently all query episodes, so a non-empty query
     *  note is the primary signal (contract: "episode has a non-empty query
     *  draft"); the fallback covers any other mode a future batch might mix in. */
    episodeDone(ep) {
        const d = this.drafts[ep.scenario_id];
        if (!d)
            return false;
        if (ep.mode === "query")
            return (d.query_text ?? "").trim().length > 0;
        if ((d.codes?.length ?? 0) > 0)
            return true;
        if (Object.keys(d.classifications ?? {}).length > 0)
            return true;
        return Object.values(d.navigateAnswers ?? {}).some((v) => Array.isArray(v) ? v.length > 0 : !!v);
    }
    openSubmitConfirm() {
        this.submitAnyway = false;
        this.showSubmitConfirm = true;
    }
    cancelSubmitConfirm() {
        this.showSubmitConfirm = false;
    }
    async confirmSubmit() {
        this.showSubmitConfirm = false;
        await this.submit();
    }
    async submit() {
        this.submitting = true;
        this.submitError = "";
        try {
            const items = this.episodes.map((ep) => ({ scenario_id: ep.scenario_id, draft: this.drafts[ep.scenario_id] }));
            const r = await api.submitBatch(this.batchId, items);
            this.result = { ticket_id: r.ticket_id, episodes: r.episodes ?? items.length };
        }
        catch (e) {
            console.error("batch submit failed:", e);
            this.submitError = "Your responses are saved. The submission could not be sent just now. "
                + "Please try again in a few minutes, and contact student support if it keeps happening.";
        }
        finally {
            this.submitting = false;
        }
    }
    renderPanel(ep) {
        switch (ep.mode) {
            case "classify": return html `<mode-classify .findings=${ep.findings ?? []}></mode-classify>`;
            case "abstract": return html `<mode-abstract .edition=${ep.edition ?? "13e"}></mode-abstract>`;
            case "audit": return html `<mode-audit .presented=${ep.presented_codes ?? []} .edition=${ep.edition ?? "13e"}></mode-audit>`;
            case "sequence": return html `<mode-sequence .unsequenced=${ep.unsequenced_codes ?? []}></mode-sequence>`;
            case "query": return html `<mode-query .routing=${ep.routing}></mode-query>`;
            default: return html `<p class="empty">Mode "${ep.mode}" not supported.</p>`;
        }
    }
    render() {
        if (this.loadError)
            return html `<p class="empty" role="alert">Could not load assessment: ${this.loadError}</p>`;
        if (this.ownWork)
            return html `<own-work-view .batch=${this.ownWork}></own-work-view>`;
        if (!this.episodes.length)
            return html `<p class="empty">Loading assessment…</p>`;
        if (this.result) {
            return html `<div class="done-card">
        <h2>Submitted for assessment</h2>
        <p>Your ${this.result.episodes} responses for <b>${this.batchTitle}</b> have been sent to an assessor for marking.
           You can close this window. Your result will appear once the assessor has reviewed your work.</p>
      </div>`;
        }
        const ep = this.episodes[this.idx];
        const sc = scenario.value;
        const docs = (sc?.documents ?? []);
        const currentDoc = (this.selectedDocId ? docs.find((d) => d.id === this.selectedDocId) : docs[0]) ?? null;
        const last = this.idx === this.episodes.length - 1;
        return html `
      <div class="stepper">
        <span class="title">${this.batchTitle}</span>
        <span class="count">Episode ${this.idx + 1} of ${this.episodes.length}</span>
        <div class="dots">
          ${this.episodes.map((e, i) => html `<button
              class="dot ${i === this.idx ? "current" : ""} ${this.episodeDone(e) ? "done" : ""}"
              title=${e.episode_title} @click=${() => this._activate(i)}>${i + 1}</button>`)}
        </div>
      </div>
      <div class="ep-title"><b>${ep.episode_title}</b></div>
      <div class="body">
        <div class="pane nav">
          ${docs.length
            ? html `<chart-navigator .documents=${docs} .selectedId=${this.selectedDocId}
                     @doc-focus=${this.onDocFocus}></chart-navigator>`
            : html `<p class="empty">No chart documents.</p>`}
        </div>
        <div class="pane chart">
          ${currentDoc ? html `<chart-viewer .document=${currentDoc}></chart-viewer>` : html `<p class="empty">Select a document.</p>`}
        </div>
        <div class="pane panel">${this.renderPanel(ep)}</div>
      </div>
      <div class="actions">
        <span class="save">${this.saveStatus === "saving" ? "Saving…" : this.saveStatus === "saved" ? "Saved" : this.saveStatus === "error" ? "Save failed" : ""}</span>
        ${this.submitError ? html `<span class="submit-error">${this.submitError}</span>` : ""}
        <span class="spacer"></span>
        <button class="nav-btn" ?disabled=${this.idx === 0} @click=${() => this._activate(this.idx - 1)}>← Previous</button>
        ${last
            ? html `<button class="submit" ?disabled=${this.submitting} @click=${this.openSubmitConfirm}>
                   ${this.submitting ? "Submitting…" : `Submit all ${this.episodes.length} for assessment`}</button>`
            : html `<button class="nav-btn" @click=${() => this._activate(this.idx + 1)}>Next →</button>`}
      </div>
      ${this.showSubmitConfirm ? this.renderSubmitConfirm() : ""}
    `;
    }
    renderSubmitConfirm() {
        const anyEmpty = this.episodes.some((e) => !this.episodeDone(e));
        const blocked = anyEmpty && !this.submitAnyway;
        return html `
      <div class="confirm-backdrop" role="dialog" aria-modal="true" aria-labelledby="batch-confirm-title">
        <div class="confirm-modal">
          <h2 id="batch-confirm-title">Submit all ${this.episodes.length} for assessment?</h2>
          <ul class="confirm-list">
            ${this.episodes.map((e) => {
            const filled = this.episodeDone(e);
            return html `<li>
                <span>${e.episode_title}</span>
                <span class="confirm-status ${filled ? "filled" : "empty"}">${filled ? "Filled" : "Empty"}</span>
              </li>`;
        })}
          </ul>
          ${anyEmpty
            ? html `
              <p class="confirm-warning">This is a one-attempt assessment marked by an assessor, so
                 once it's sent you can't go back and finish anything left empty.</p>
              <label class="confirm-anyway">
                <input type="checkbox" .checked=${this.submitAnyway}
                       @change=${(ev) => { this.submitAnyway = ev.target.checked; }} />
                <span>Submit anyway, I understand some responses are empty</span>
              </label>`
            : ""}
          <div class="confirm-actions">
            <button class="nav-btn" @click=${this.cancelSubmitConfirm}>Cancel</button>
            <button class="submit" ?disabled=${blocked} @click=${this.confirmSubmit}>Submit</button>
          </div>
        </div>
      </div>`;
    }
};
__decorate([
    property({ type: String })
], BatchView.prototype, "batchId", void 0);
__decorate([
    state()
], BatchView.prototype, "batchTitle", void 0);
__decorate([
    state()
], BatchView.prototype, "episodes", void 0);
__decorate([
    state()
], BatchView.prototype, "idx", void 0);
__decorate([
    state()
], BatchView.prototype, "selectedDocId", void 0);
__decorate([
    state()
], BatchView.prototype, "saveStatus", void 0);
__decorate([
    state()
], BatchView.prototype, "result", void 0);
__decorate([
    state()
], BatchView.prototype, "loadError", void 0);
__decorate([
    state()
], BatchView.prototype, "submitting", void 0);
__decorate([
    state()
], BatchView.prototype, "submitError", void 0);
__decorate([
    state()
], BatchView.prototype, "showSubmitConfirm", void 0);
__decorate([
    state()
], BatchView.prototype, "submitAnyway", void 0);
__decorate([
    state()
], BatchView.prototype, "ownWork", void 0);
BatchView = __decorate([
    customElement("batch-view")
], BatchView);
export { BatchView };
